import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { analytics } from "@/lib/analytics";

/**
 * Landing proof carousel v2 — CSS scroll-snap (NO carousel lib; native swipe
 * on mobile, arrows on desktop, dots always).
 *
 * PT: 10 positions — 9 image slides from /landing/v2/ (art v2, 14/07: the v1
 * landing-02..08 set carried wrong product info and was removed) + a React
 * CTA card as position 10 (the most engaged visitor on the page gets a
 * click). EN/NL: ONLY the existing locale poster (posters/ pipeline) — a
 * single-slide array renders with NO carousel chrome (no dots/arrows/IO).
 * When translated art lands, point the locale at its own array below.
 *
 * Assets are under /landing/v2/ with NEW names on purpose: /public has no
 * content hashing, so reusing v1 names would serve stale cache to returning
 * visitors. Variants per slide: {id}-1080.webp (fallback src) +
 * {id}-{470|940}.{avif|webp}. Slides 08/09 originate from 819px sources —
 * their "940/1080" files are really 819px (never upscaled) and their srcSet
 * descriptors say so honestly.
 *
 * Below the main hero: all gallery art lazy-loads with reserved dimensions.
 * Scroll behavior respects the current reduced-motion preference.
 */

export interface LandingSlideV2 {
  id: string;
  /** Real PT-BR description of the art (a11y + SEO). */
  alt: string;
  /** Native dimensions of the largest variant (drives width/height + srcSet). */
  w: number;
  h: number;
  /** Actual pixel width of the "-940" variant (819 for the mirror slides). */
  w940: number;
}

// Order = carousel order; reorder = move a line. Files: /landing/v2/{id}-*.
export const LANDING_SLIDES_PT: LandingSlideV2[] = [
  {
    id: "slide-01",
    alt: "Painel VYV 'Você não precisa de 10 apps' — mulher diante do espelho digital cercada pelos módulos Check-in, Fitness, Nutrição, Sono, Hábitos, Metas, Calendário, Agenda e Tarefas, Cérebro e Idiomas, com integrações Google Calendar, Apple Health, Strava, WhatsApp e Notion",
    w: 1080, h: 1920, w940: 940,
  },
  {
    id: "slide-02",
    alt: "O cérebro VYV dentro do WhatsApp — homem sorrindo ao lado de uma conversa real: resumo do dia, criação de tarefa por mensagem e relatório de alimentação, com privacidade criptografada de ponta",
    w: 1080, h: 1920, w940: 940,
  },
  {
    id: "slide-03",
    alt: "Seu treino, sua voz — homem na academia registrando o treino por áudio com a Vivi: exercícios, séries, calorias e resumo da semana com evolução de doze por cento",
    w: 1080, h: 1920, w940: 940,
  },
  {
    id: "slide-04",
    alt: "Nutrição inteligente, resultados reais — mulher em um café fotografando a refeição; o VYV reconhece os alimentos e calcula calorias, proteínas, carboidratos e água, com dicas personalizadas",
    w: 1080, h: 1920, w940: 940,
  },
  {
    id: "slide-05",
    alt: "Todas as suas agendas, um só lugar — mulher no sofá com o dia organizado: Google, Outlook e Apple Agenda unificados com tarefas, lembretes inteligentes e resumo do dia",
    w: 1080, h: 1920, w940: 940,
  },
  {
    id: "slide-06",
    alt: "Idiomas sem limites — Modo Professor do VYV com correção de pronúncia, vocabulário novo e exercícios diários, em inglês, português, espanhol, francês, alemão, italiano e mais",
    w: 1080, h: 1920, w940: 940,
  },
  {
    id: "slide-07",
    alt: "O cérebro VYV no WhatsApp — mulher sorrindo ao lado de uma conversa com lembrete de água a cada duas horas e relatório de alimentação da semana",
    w: 1080, h: 1920, w940: 940,
  },
  {
    id: "slide-08",
    alt: "Calendário Inteligente — homem diante do espelho VYV organizando reuniões, tarefas e compromissos por voz, com a agenda de hoje e os próximos compromissos ao redor",
    w: 819, h: 1456, w940: 819,
  },
  {
    id: "slide-09",
    alt: "Check-in Diário — mulher diante do espelho VYV contando como está: emoções, energia do dia e foco de hoje registrados sem esforço",
    w: 819, h: 1456, w940: 819,
  },
];

// Locale → image slides. EN/NL have no v2 art yet: empty array = poster-only
// single-slide mode (no chrome). Drop translated art in /landing/v2/ and
// point the locale at its own array — nothing else changes.
export const SLIDES_BY_LOCALE: Record<"pt" | "en" | "nl", LandingSlideV2[]> = {
  pt: LANDING_SLIDES_PT,
  en: [],
  nl: [],
};

function slideSources(slide: LandingSlideV2) {
  const base = "/landing/v2/" + slide.id;
  return {
    avif: base + "-470.avif 470w, " + base + "-940.avif " + slide.w940 + "w",
    webp: base + "-470.webp 470w, " + base + "-940.webp " + slide.w940 + "w",
    fallback: base + "-1080.webp",
  };
}

interface LandingCarouselProps {
  // Arte de poster existe só em pt/en/nl. Decisão nº 13: idioma novo HERDA o
  // pt até engajar — o chamador resolve, este componente só recebe o que existe.
  posterLoc: "pt" | "en" | "nl";
  /** EN/NL single slide — the existing posters/ pipeline markup, untouched. */
  posterSlide: React.ReactNode;
  labels: {
    prev: string;
    next: string;
    goTo: (n: number) => string;
    ctaHeadline: string;
    ctaButton: string;
  };
  /** Position-10 CTA click (fires carousel_end_cta_click + navigates /app). */
  onEndCta: () => void;
}

const frameStyle: React.CSSProperties = {
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
};

export function LandingCarousel({ posterLoc, posterSlide, labels, onEndCta }: LandingCarouselProps) {
  const slides = SLIDES_BY_LOCALE[posterLoc];
  const total = slides.length === 0 ? 1 : slides.length + 1; // +1 = CTA card
  const trackRef = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(0);
  const viewedRef = useRef<Set<number>>(new Set());
  const lastProgrammaticRef = useRef(0);
  const lastIndexRef = useRef(0);

  const scrollToIndex = useCallback((index: number, type: "arrow" | "dot") => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(total - 1, index));
    lastProgrammaticRef.current = Date.now();
    analytics.landingCarouselInteraction(type);
    track.scrollTo({ left: clamped * track.clientWidth, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }, [total]);

  // Slide visibility: >=60% marks viewed (once per session) + drives dots.
  // An index change NOT preceded by an arrow/dot click within 800ms = swipe.
  useEffect(() => {
    const track = trackRef.current;
    if (!track || slides.length === 0) return;
    const children = Array.from(track.children);
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = children.indexOf(entry.target);
          if (index < 0) continue;
          setCurrent(index);
          if (!viewedRef.current.has(index)) {
            viewedRef.current.add(index);
            const slideId = index < slides.length ? slides[index].id : "cta-card";
            analytics.landingCarouselSlideViewed(index, slideId);
          }
          if (index !== lastIndexRef.current) {
            lastIndexRef.current = index;
            if (Date.now() - lastProgrammaticRef.current > 800) {
              analytics.landingCarouselInteraction("swipe");
            }
          }
        }
      },
      { root: track, threshold: 0.6 },
    );
    children.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [slides]);

  // EN/NL (or any locale without v2 art): the poster alone, no chrome.
  if (slides.length === 0) {
    return <>{posterSlide}</>;
  }

  return (
    <div className="relative" role="region" aria-roledescription="carousel">
      <div
        ref={trackRef}
        className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth rounded-3xl [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none" }}
      >
        {slides.map((slide, i) => {
          const src = slideSources(slide);
          return (
            <div key={slide.id} className="w-full shrink-0 snap-center">
              <div className="overflow-hidden rounded-3xl" style={frameStyle}>
                <picture>
                  <source type="image/avif" srcSet={src.avif} sizes="(max-width: 480px) 90vw, 448px" />
                  <source type="image/webp" srcSet={src.webp} sizes="(max-width: 480px) 90vw, 448px" />
                  <img
                    src={src.fallback}
                    alt={slide.alt}
                    width={slide.w}
                    height={slide.h}
                    loading="lazy"
                    decoding="async"
                    className="block h-auto w-full"
                    draggable={false}
                  />
                </picture>
              </div>
            </div>
          );
        })}

        {/* Position 10 — CTA card (React, no image): the visitor who reached
            the end of the carousel is the hottest one on the page. */}
        <div className="w-full shrink-0 snap-center">
          <div
            className="flex h-full min-h-[420px] flex-col items-center justify-center gap-6 rounded-3xl px-8 py-16 text-center"
            style={{
              ...frameStyle,
              background: "linear-gradient(180deg, #050811 0%, #0a0e1a 60%, #050811 100%)",
            }}
          >
            <p className="font-display text-2xl font-bold leading-snug text-foreground sm:text-3xl">
              {labels.ctaHeadline}
            </p>
            <button
              type="button"
              onClick={onEndCta}
              className="inline-flex items-center gap-2 rounded-xl px-7 py-3.5 font-display text-[15px] font-semibold text-white transition-transform duration-200 active:scale-95"
              style={{
                background: "linear-gradient(90deg, hsl(187 72% 45%), hsl(265 60% 55%))",
                boxShadow: "0 10px 40px hsl(187 72% 50% / 0.35)",
              }}
            >
              {labels.ctaButton}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Desktop arrows */}
      <button
        type="button"
        onClick={() => scrollToIndex(current - 1, "arrow")}
        aria-label={labels.prev}
        disabled={current === 0}
        className="absolute left-2 top-1/2 hidden -translate-y-1/2 rounded-full min-w-11 min-h-11 p-3 transition-opacity disabled:opacity-30 sm:block"
        style={{ background: "rgba(5,8,17,0.72)", border: "1px solid rgba(255,255,255,0.14)" }}
      >
        <ChevronLeft size={18} className="text-white/85" />
      </button>
      <button
        type="button"
        onClick={() => scrollToIndex(current + 1, "arrow")}
        aria-label={labels.next}
        disabled={current === total - 1}
        className="absolute right-2 top-1/2 hidden -translate-y-1/2 rounded-full min-w-11 min-h-11 p-3 transition-opacity disabled:opacity-30 sm:block"
        style={{ background: "rgba(5,8,17,0.72)", border: "1px solid rgba(255,255,255,0.14)" }}
      >
        <ChevronRight size={18} className="text-white/85" />
      </button>

      {/* Dots */}
      <div className="mt-4 flex items-center justify-center gap-0">
        {Array.from({ length: total }, (_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => scrollToIndex(i, "dot")}
            aria-label={labels.goTo(i + 1)}
            aria-current={current === i ? "true" : undefined}
            className="flex h-11 min-w-0 flex-1 max-w-11 items-center justify-center rounded-md"
          ><span aria-hidden="true" className="rounded-full" style={{ width: current === i ? 18 : 6, height: 6, background: current === i ? "#bceeea" : "#8da8ba" }} /></button>
        ))}
      </div>
    </div>
  );
}
